<script>
import { computed } from 'vue';

import { PlusCircleIcon } from '@thehotelsnetwork/icons/vue/linear';

import useMainState from '@/state/useMainState';
import useUserState from '@/state/useUserState';
import useSectionState from '@/state/useSectionState';
import handleTrackFilter from '@/utils/handleTrackFilter';

import performAccountNavigation from '@/utils/performAccountNavigation';

import {
  currentCompsetsOption,
} from '@/config/sectionFiltersConfig';

export default {
  components: { PlusCircleIcon },
  props: {
    config: {
      type: Object,
      default: () => ({}),
    },
    tabs: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const { isOverview } = useMainState();
    const { isStrictlyDMOUser } = useUserState();
    const {
      currentPermissions,
      userHasCustomCompsets,
      userHasDestinations,
      userHasClusters,
      userHasBrands,
      sectionName,
      compsetsFilter,
    } = useSectionState();

    const disableClustersPremium = computed(() => currentCompsetsOption.value.match(/cluster/)
      && !currentPermissions.value.cluster
      && userHasClusters.value);

    const disableBrandsPremium = computed(() => currentCompsetsOption.value.match(/brand/)
      && !currentPermissions.value.cluster
      && userHasBrands.value);

    const disableCompsetsPremium = computed(() => currentCompsetsOption.value.match(/custom/)
      && sectionName.value !== 'rates'
      && !currentPermissions.value.compsets
      && userHasCustomCompsets.value);

    const disableDestinationsPremium = computed(
      () => currentCompsetsOption.value.match(/destination/)
      && !currentPermissions.value.destination
      && userHasDestinations.value,
    );

    const disablePremium = computed(() => [
      disableClustersPremium.value,
      disableBrandsPremium.value,
      disableCompsetsPremium.value,
      disableDestinationsPremium.value,
    ].some(Boolean));

    const upgradeBtnSection = computed(() => `${currentCompsetsOption.value} - compsets filter`);

    const disableOption = (option) => (option.status === 'rates_only' && sectionName.value !== 'rates')
      || !option.ready;

    return {
      isOverview,
      isStrictlyDMOUser,
      disablePremium,
      currentCompsetsOption,
      disableCompsetsPremium,
      disableDestinationsPremium,
      disableClustersPremium,
      disableBrandsPremium,
      userHasCustomCompsets,
      userHasDestinations,
      userHasClusters,
      userHasBrands,
      handleTrackFilter,
      performAccountNavigation,
      upgradeBtnSection,
      disableOption,
      compsetsFilter,
    };
  },
};
</script>

<template>
  <LpFilterSelect
    :key="compsetsFilter"
    v-model="compsetsFilter"
    data-testid="filterDropdown"
    :options="config.options"
    :placeholder="config.placeholder"
    :search-placeholder="config.searchPlaceholder"
    :searchable="true"
    :show-select-all="!disablePremium"
    menu-width="35rem"
    menu-max-height="35rem"
    x-position="right"
    :min-selected-options="1"
    disable-clear
    @update:model-value="handleTrackFilter('Compset')"
  >
    <template
      v-if="!isStrictlyDMOUser"
      #menuheader
    >
      <div class="pt-2">
        <LpHorizontalMenu
          v-model="currentCompsetsOption"
        >
          <LpHorizontalMenuItem
            v-for="item in tabs"
            :key="item.value"
            :value="item.value"
          >
            {{ $t(item.label) }}
          </LpHorizontalMenuItem>
        </LpHorizontalMenu>
        <LpCard
          v-if="disablePremium"
          class="flex gap-3 p-4 mt-2 shadow-none bg-grape-100 text-grape-500"
        >
          <span v-if="disableCompsetsPremium">
            {{ $t('Upgrade your BenchDirect plan to view your Custom Compsets in your Dashboard') }}
          </span>
          <span v-if="disableDestinationsPremium">
            {{ $t('Upgrade your BenchDirect plan to view your Custom Destinations in your Dashboard') }}
          </span>
          <span v-if="disableClustersPremium">
            {{ $t('Upgrade your BenchDirect plan to view Clusters in your Dashboard') }}
          </span>
          <span v-if="disableBrandsPremium">
            {{ $t('Upgrade your BenchDirect plan to view Brands in your Dashboard') }}
          </span>
          <div class="flex flex-col justify-center items-center">
            <UpgradeBtn :section="upgradeBtnSection" />
          </div>
        </LpCard>
      </div>
    </template>

    <template
      #default="{
        option,
        click,
        active,
        disabled = disableOption(option) || disablePremium,
      }"
    >
      <div
        :class="[
          'px-2 rounded',
          { 'pointer-events-none': disablePremium },
        ]"
      >
        <LpMarkerBtn
          @click.prevent="() => !disabled && click()"
        >
          <LpCheckbox
            :model-value="active"
            :disabled="disabled"
            :input-id="option.value"
            :square-color="option.color"
          >
            <span :class="{ 'text-base-500': disabled }">
              {{ option.label }}
            </span>
            <LpTag
              v-if="option.status === 'rates_only'"
              color="warning"
              size="small"
              class="ml-2"
            >
              <span class="px-2 text-xs font-light text-base-900">
                {{ $t('Rates-only') }}
              </span>
            </LpTag>
          </LpCheckbox>
        </LpMarkerBtn>
      </div>
    </template>
    <template
      #empty
    >
      <div
        v-if="!userHasCustomCompsets || !userHasDestinations || !userHasClusters"
        class="flex flex-col justify-center items-center"
      >
        <PlusCircleIcon
          alt="First Custom Compset"
          class="my-4 w-12 h-12 text-primary-500"
        />

        <div
          v-if="currentCompsetsOption.match(/destination/)"
          class="flex flex-col justify-center items-center"
        >
          <span
            class="px-14 text-lg font-bold text-center font-display"
          >
            {{ $t('Seems like there isn’t any Destination created yet') }}
          </span>
          <router-link
            to="/property/compsets/destinations"
          >
            <LpBasicLink>{{ $t("Create one now") }}</LpBasicLink>
          </router-link>
        </div>

        <div
          v-if="currentCompsetsOption.match(/custom/)"
          class="flex flex-col justify-center items-center"
        >
          <span
            class="px-14 text-lg font-bold text-center font-display"
          >
            {{ $t('Seems like there isn’t any Custom Compset created yet') }}
          </span>
          <router-link
            to="/property/compsets/custom"
          >
            <LpBasicLink>{{ $t("Create one now") }}</LpBasicLink>
          </router-link>
        </div>

        <div
          v-if="currentCompsetsOption.match(/cluster|brand/)"
          class="flex flex-col justify-center items-center"
        >
          <span
            class="px-14 text-lg font-bold text-center font-display"
          >
            <template v-if="currentCompsetsOption.match(/cluster/)">
              {{ $t('Seems like there isn’t any Cluster created yet') }}
            </template>

            <template v-if="currentCompsetsOption.match(/brand/)">
              {{ $t('Seems like there isn’t any Brand created yet') }}
            </template>
          </span>
          <LpBasicLink @click="performAccountNavigation(`/${currentCompsetsOption}s`)">
            {{ $t("Go to account settings") }}
          </LpBasicLink>
        </div>
      </div>
      <div v-else />
    </template>
  </LpFilterSelect>
</template>
